import React, { useState } from "react";
import { Play, X, Sparkles } from "lucide-react";

// Phase 36.14 — Reusable explainer-video embed.
//
// Usage:
//   <VideoExplainer videoUrl="https://www.loom.com/embed/abc123" title="DARA in 90s" />
//   <VideoExplainer videoUrl="https://www.youtube.com/embed/dQw4w9WgXcQ" />
//   <VideoExplainer videoUrl="/videos/dara-tour.mp4" mediaType="mp4" />
//
// Click-to-play (no autoplay, no preload) so cellular visitors aren't billed.
// `localStorage.{storageKey}=seen` after first dismiss so we can also use
// this as a first-visit overlay (see <VideoExplainerOverlay/> below).

function detectType(url = "") {
  if (!url) return "iframe";
  if (url.endsWith(".mp4") || url.endsWith(".webm") || url.endsWith(".mov")) return "mp4";
  return "iframe";
}

export function VideoExplainer({ videoUrl, title = "DARA in 90 seconds", thumbnail, accent = "#FFB000", mediaType, testId = "video-explainer" }) {
  const [playing, setPlaying] = useState(false);
  const type = mediaType || detectType(videoUrl);

  if (!videoUrl) return null;

  return (
    <div
      data-testid={testId}
      className="relative w-full aspect-video bg-zinc-950 border border-zinc-900 overflow-hidden group"
    >
      {!playing && (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          data-testid={`${testId}-play-btn`}
          className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer"
          style={{ background: thumbnail ? `url(${thumbnail}) center/cover` : "#0a0a0a" }}
        >
          <span
            className="absolute inset-0"
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.85) 100%)" }}
          />
          <span
            className="relative z-10 w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
            style={{ background: accent, color: "#000", boxShadow: `0 0 32px ${accent}33` }}
          >
            <Play className="w-7 h-7 ml-1" />
          </span>
          <span className="relative z-10 mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
            // {title}
          </span>
          <span className="relative z-10 mt-1 font-mono text-[10px] text-zinc-500">click to play</span>
        </button>
      )}
      {playing && type === "iframe" && (
        <iframe
          title={title}
          src={`${videoUrl}${videoUrl.includes("?") ? "&" : "?"}autoplay=1`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0"
          data-testid={`${testId}-iframe`}
        />
      )}
      {playing && type === "mp4" && (
        <video
          src={videoUrl}
          autoPlay
          controls
          className="absolute inset-0 w-full h-full object-contain bg-black"
          data-testid={`${testId}-video`}
        />
      )}
    </div>
  );
}

// First-visit dismissable overlay — pinned for /app/today.
const SEEN_KEY = "dara_explainer_seen_v1";
export function VideoExplainerOverlay({ videoUrl, title = "Welcome to DARA", description }) {
  const seen = typeof window !== "undefined" && localStorage.getItem(SEEN_KEY) === "true";
  const [open, setOpen] = useState(!seen && !!videoUrl);

  const close = () => {
    if (typeof window !== "undefined") localStorage.setItem(SEEN_KEY, "true");
    setOpen(false);
  };

  if (!open || !videoUrl) return null;

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/85 backdrop-blur flex items-center justify-center p-4"
      data-testid="video-explainer-overlay"
    >
      <div className="border border-zinc-800 bg-zinc-950 max-w-3xl w-full">
        <div className="flex items-center justify-between p-4 border-b border-zinc-900">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FFB000]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] font-bold">
              // {title}
            </span>
          </div>
          <button
            onClick={close}
            data-testid="video-overlay-dismiss"
            className="text-zinc-500 hover:text-white p-1"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          <VideoExplainer videoUrl={videoUrl} title={title} />
          {description && (
            <p className="text-sm text-zinc-400 mt-4 leading-relaxed">{description}</p>
          )}
          <div className="mt-4 flex items-center justify-end gap-3">
            <button
              onClick={close}
              data-testid="video-overlay-skip"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white"
            >
              Skip for now →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoExplainer;
